/**
 * @fileoverview Layout for period charts drawn in step with a period table.
 *
 * The financial worksheet renders each chart directly beneath a table whose
 * first column is a fixed-width metric gutter and whose remaining width is
 * split equally between the period columns (MOR-1282). Reserving the same
 * gutter for the Y axis and dropping the side margins makes the plot area the
 * period columns' span. Charts with bars then get a band category scale by
 * default, which centres each period in its column. Line-only charts default
 * to a point scale that pins the first and last periods to the plot edges, so
 * they plot against {@link periodIndexAxis} instead. (An explicit
 * `scale="band"` is no fix: recharts then offsets ticks by 2px rather than
 * half a band, leaving the labels at the band edges.)
 */

export interface PeriodAlignedChartProps {
  /**
   * Width (px) reserved left of the plot area — the paired table's metric
   * column. Omitted, the chart keeps its standalone layout.
   */
  plotInsetLeft?: number;
}

/** Margin and Y-axis width passed to a period chart's recharts root and `YAxis`. */
export interface PeriodAlignedLayout {
  /** Omitted in standalone mode so recharts applies its default margin. */
  margin?: { top: number; right: number; bottom: number; left: number };
  yAxisWidth: number;
}

/**
 * Returns the chart layout for a period chart.
 * @param plotInsetLeft Width of the paired table's metric column; omit for standalone charts.
 * @param standaloneYAxisWidth Y-axis width used when `plotInsetLeft` is omitted.
 * @returns Zero side margins and a `plotInsetLeft`-wide Y axis when aligned; otherwise only the standalone width.
 */
export function periodAlignedLayout(
  plotInsetLeft: number | undefined,
  standaloneYAxisWidth: number,
): PeriodAlignedLayout {
  if (plotInsetLeft == null) {
    return { yAxisWidth: standaloneYAxisWidth };
  }
  return {
    margin: { top: 5, right: 0, bottom: 5, left: 0 },
    yAxisWidth: plotInsetLeft,
  };
}

/**
 * Numeric X-axis props that place period `i` at the centre of the i-th of
 * `periods.length` equal bands — for line-only charts, whose category axis
 * would otherwise be a point scale. Plot rows carry their index as `index`.
 */
export function periodIndexAxis(periods: ReadonlyArray<string>) {
  return {
    type: "number" as const,
    dataKey: "index",
    domain: [-0.5, periods.length - 0.5],
    ticks: periods.map((_, i) => i),
    interval: 0 as const,
    allowDecimals: false,
    tickFormatter: (i: number) => periods[i] ?? "",
  };
}
